import { notification } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiChevronLeft } from "react-icons/bi";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "shared/components/Header";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  EXECUTE_INSURANCE_PLAN_IMPORT,
  VALIDATE_INSURANCE_PLAN_IMPORT,
} from "utils/api/graphql/mutations/insurance-plans";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import {
  INITIAL_VALUES,
  INSURANCE_PLAN_RISKS,
  SCREENS,
} from "utils/constants";
import {
  DEFAULT_RISK,
  IMPORT_STEPS,
  IMPORT_STRATEGIES,
  REFERENTIAL_TYPES,
} from "./constants";
import StepAnalysis from "./StepAnalysis";
import StepImporting from "./StepImporting";
import StepMapping from "./StepMapping";
import StepResult from "./StepResult";
import StepUpload from "./StepUpload";
import StepValidation from "./StepValidation";
import StepValueMapping from "./StepValueMapping";

const STEPS = [
  IMPORT_STEPS.UPLOAD,
  IMPORT_STEPS.MAPPING,
  IMPORT_STEPS.VALIDATION,
  IMPORT_STEPS.VALUE_MAPPING,
  IMPORT_STEPS.ANALYSIS,
  IMPORT_STEPS.IMPORTING,
  IMPORT_STEPS.RESULT,
];

const ImportWizard = ({ onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const IMPORT = t("IMPORT", { returnObjects: true });
  const { state: locationState } = useLocation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [fileData, setFileData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [strategy, setStrategy] = useState(IMPORT_STRATEGIES.ALL);
  const [importResult, setImportResult] = useState(null);
  const [analysisConfirmed, setAnalysisConfirmed] = useState(false);
  const [valueMappingState, setValueMappingState] = useState({
    maps: {},
    save: {},
    refNames: {},
    selectedRefIds: {},
  });
  const [valueMappings, setValueMappings] = useState(null);

  const [validateImport, { loading: validating }] = useMutation(
    VALIDATE_INSURANCE_PLAN_IMPORT,
  );

  const [executeImport] = useMutation(EXECUTE_INSURANCE_PLAN_IMPORT, {
    refetchQueries: [
      {
        query: INSURANCE_PLANS,
        awaitRefetchQueries: true,
        variables: INITIAL_VALUES,
      },
    ],
  });

  const stepName = STEPS[currentStep];

  const stepLabels = STEPS.map((step) => {
    const labelMap = {
      [IMPORT_STEPS.UPLOAD]: IMPORT.STEPS.UPLOAD,
      [IMPORT_STEPS.MAPPING]: IMPORT.STEPS.MAPPING,
      [IMPORT_STEPS.VALIDATION]: IMPORT.STEPS.VALIDATION,
      [IMPORT_STEPS.VALUE_MAPPING]: IMPORT.STEPS?.VALUE_MAPPING || "Values",
      [IMPORT_STEPS.ANALYSIS]: IMPORT.STEPS.ANALYSIS,
      [IMPORT_STEPS.IMPORTING]: IMPORT.STEPS.IMPORTING,
      [IMPORT_STEPS.RESULT]: IMPORT.STEPS.RESULT,
    };
    return labelMap[step] || step;
  });

  const visibleStepIndex = STEPS.indexOf(stepName);

  const handleFileUpload = async (base64, filename) => {
    setFileData({ base64, filename });
    try {
      const { data } = await validateImport({
        variables: {
          data: { base64, filename },
        },
      });
      const result = get(data, "validateInsurancePlanImport");
      setValidationResult(result);
      setMapping(result.autoMapping || result.mapping || {});
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.MAPPING));
    } catch (error) {
      notification.error({
        message: IMPORT.ERRORS.UPLOAD_FAILED,
        description: error.message,
      });
    }
  };

  const handleMappingConfirm = async (updatedMapping) => {
    setMapping(updatedMapping);
    try {
      const { data } = await validateImport({
        variables: {
          data: {
            base64: fileData.base64,
            filename: fileData.filename,
            mapping: updatedMapping,
          },
        },
      });
      const result = get(data, "validateInsurancePlanImport");
      setValidationResult(result);
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.VALIDATION));
    } catch (error) {
      notification.error({
        message: IMPORT.ERRORS.VALIDATION_FAILED,
        description: error.message,
      });
    }
  };

  const handleValidationNext = () => {
    const distinctValues = get(validationResult, "preview.distinctValues", {});
    const hasDistinctRisks =
      distinctValues.risk && distinctValues.risk.length > 0;
    if (hasDistinctRisks) {
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.VALUE_MAPPING));
    } else {
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.ANALYSIS));
    }
  };

  const handleValueMappingConfirm = (mappings) => {
    setValueMappings(mappings);
    setCurrentStep(STEPS.indexOf(IMPORT_STEPS.ANALYSIS));
  };

  const buildRiskSections = () => {
    const distinctValues = get(validationResult, "preview.distinctValues", {});
    const riskValues = distinctValues.risk || [];
    if (riskValues.length === 0) return [];

    const VM = IMPORT.VALUE_MAPPING || {};
    const optionLabels = get(VM, "OPTIONS.RISK", {});
    const riskOptions = Object.entries(INSURANCE_PLAN_RISKS).map(
      ([key, value]) => ({
        label: optionLabels[value] || key,
        value,
      }),
    );

    return [
      {
        key: "risk",
        title: VM.SECTIONS?.RISK || "Risk",
        fileValues: riskValues,
        options: riskOptions,
        referentialType: REFERENTIAL_TYPES.RISK,
      },
    ];
  };

  const handleAnalysisNext = () => {
    handleExecuteImport();
  };

  const handleExecuteImport = async () => {
    setCurrentStep(STEPS.indexOf(IMPORT_STEPS.IMPORTING));
    try {
      const { data } = await executeImport({
        variables: {
          data: {
            base64: fileData.base64,
            filename: fileData.filename,
            mapping,
            strategy,
            risk: locationState?.risk || DEFAULT_RISK,
            ...(valueMappings ? { valueMappings } : {}),
          },
        },
      });
      const result = get(data, "executeInsurancePlanImport");
      if (result) {
        setImportResult(result);
      } else {
        setImportResult({
          success: false,
          errors: [{ key: "UNEXPECTED_ERROR", params: {} }],
        });
      }
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.RESULT));
    } catch (error) {
      const gqlMsg = get(error, "graphQLErrors[0].message", "");
      const msg = gqlMsg || error.message || "";
      let parsedError;
      try {
        parsedError = JSON.parse(msg);
      } catch (_e) {
        parsedError = null;
      }
      const errorObj = parsedError?.key
        ? parsedError
        : { key: "UNEXPECTED_ERROR", params: { message: msg } };
      setImportResult({
        success: false,
        errors: [errorObj],
      });
      setCurrentStep(STEPS.indexOf(IMPORT_STEPS.RESULT));
    }
  };

  const handleFinish = () => {
    navigate(SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.LIST.path);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setFileData(null);
    setValidationResult(null);
    setMapping({});
    setStrategy(IMPORT_STRATEGIES.ALL);
    setImportResult(null);
    setAnalysisConfirmed(false);
    setValueMappingState({
      maps: {},
      save: {},
      refNames: {},
      selectedRefIds: {},
    });
    setValueMappings(null);
  };

  const goBack = () => {
    if (currentStep === 0) {
      onBack();
      return;
    }
    const currentVisible = STEPS.indexOf(stepName);
    if (currentVisible > 0) {
      const prevStep = STEPS[currentVisible - 1];
      setCurrentStep(STEPS.indexOf(prevStep));
    }
  };

  return (
    <>
      <Header
        title={IMPORT.TITLE}
        subtitle={IMPORT.SUBTITLE}
        actions={
          stepName === IMPORT_STEPS.UPLOAD
            ? [
                {
                  text: IMPORT.BACK,
                  onClick: goBack,
                  type: "primary--outlined",
                  icon: <BiChevronLeft size={20} />,
                },
              ]
            : []
        }
      />
      <div className="pricers-import__stepper">
        {stepLabels.map((label, idx) => {
          const isActive = idx === visibleStepIndex;
          const isDone = idx < visibleStepIndex;
          return (
            <div key={idx} className="pricers-import__step-item">
              <div className="pricers-import__step-row">
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone || isActive,
                    "pricers-import__step-connector--hidden": idx === 0,
                  })}
                />
                <span
                  className={classNames("pricers-import__step-number", {
                    "pricers-import__step-number--active": isActive,
                    "pricers-import__step-number--done": isDone,
                  })}
                >
                  {isDone ? "\u2713" : idx + 1}
                </span>
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone,
                    "pricers-import__step-connector--hidden":
                      idx === stepLabels.length - 1,
                  })}
                />
              </div>
              <span
                className={classNames("pricers-import__step-label", {
                  "pricers-import__step-label--active": isActive,
                  "pricers-import__step-label--done": isDone,
                })}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {stepName === IMPORT_STEPS.UPLOAD && (
        <StepUpload onUpload={handleFileUpload} loading={validating} />
      )}
      {stepName === IMPORT_STEPS.MAPPING && validationResult && (
        <StepMapping
          fields={validationResult.fields}
          headers={validationResult.headers}
          mapping={mapping}
          columnSamples={validationResult.columnSamples}
          onConfirm={handleMappingConfirm}
          onMappingChange={setMapping}
          loading={validating}
          onBack={goBack}
          backLabel={IMPORT.BACK_CHANGE_FILE}
        />
      )}
      {stepName === IMPORT_STEPS.VALIDATION && validationResult && (
        <StepValidation
          validationResult={validationResult}
          onNext={handleValidationNext}
          onBack={goBack}
          backLabel={IMPORT.BACK_EDIT_MAPPING}
        />
      )}
      {stepName === IMPORT_STEPS.VALUE_MAPPING && validationResult && (
        <StepValueMapping
          sections={buildRiskSections()}
          onConfirm={handleValueMappingConfirm}
          persistedState={valueMappingState}
          onStateChange={setValueMappingState}
          onBack={goBack}
          backLabel={IMPORT.BACK_REVIEW_VALIDATION}
          labels={IMPORT.VALUE_MAPPING || {}}
        />
      )}
      {stepName === IMPORT_STEPS.ANALYSIS && validationResult && (
        <StepAnalysis
          preview={validationResult.preview}
          strategy={strategy}
          onStrategyChange={setStrategy}
          onNext={handleAnalysisNext}
          confirmed={analysisConfirmed}
          onConfirmedChange={setAnalysisConfirmed}
          onBack={goBack}
          backLabel={IMPORT.BACK_REVIEW_VALIDATION}
        />
      )}
      {stepName === IMPORT_STEPS.IMPORTING && <StepImporting />}
      {stepName === IMPORT_STEPS.RESULT && (
        <StepResult
          result={
            importResult || {
              success: false,
              errors: [
                {
                  key: "UNEXPECTED_ERROR",
                  params: { message: "" },
                },
              ],
            }
          }
          onFinish={handleFinish}
          onReset={handleReset}
        />
      )}
    </>
  );
};

export default ImportWizard;
